import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

/**
 * Booked Calls — Team365 → Events (Jenn, Oct 7 2026). Calls people booked
 * through "Book a Call with Jenn", read live from her Booked Calls Google
 * Calendar: upcoming first, then the last 30 days. Members link to their
 * profile. Jenn-level only (the caller checks can_manage_affiliates; the
 * server enforces it too).
 */
const fmtWhen = (iso) => new Date(iso).toLocaleString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

function CallRow({ call }) {
  const [showNotes, setShowNotes] = useState(false);
  return (
    <div className="px-5 py-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium text-reset-brown">{fmtWhen(call.start)}</p>
          <p className="text-sm text-gray-600 mt-0.5">
            {call.people.length === 0 ? <span className="text-gray-400">{call.title}</span> : call.people.map((p, i) => (
              <span key={p.email || i}>
                {i > 0 && ', '}
                {p.memberId
                  ? <Link to={`/admin/members/${p.memberId}`} className="font-medium text-reset-terracotta hover:underline">{p.name}</Link>
                  : <>{p.name} <span className="text-xs text-gray-400">(not a member yet)</span></>}
              </span>
            ))}
          </p>
        </div>
        {call.confirmation && (
          <span className={`text-xs shrink-0 ${call.confirmation.problem ? 'text-reset-terracotta' : 'text-reset-sage'}`} title={call.confirmation.problem || ''}>
            {call.confirmation.problem ? 'Our confirmation did not send' : 'Our confirmation sent'}
          </span>
        )}
        {call.notes && (
          <button onClick={() => setShowNotes(s => !s)} className="text-xs font-medium text-reset-terracotta hover:underline shrink-0">
            {showNotes ? 'Hide what they wrote' : 'What they wrote'}
          </button>
        )}
      </div>
      {showNotes && <p className="text-sm text-gray-600 mt-2 whitespace-pre-line bg-reset-cream rounded-xl px-4 py-3">{call.notes}</p>}
    </div>
  );
}

/**
 * Our own confirmation email (Jenn, 2026-10-28: "why aren't we sending the
 * personalized confirmation rather than google?"). On/off, the topic replies
 * (Jenn's words, editable here), and a sample sent to whoever presses it.
 */
function ConfirmationSettings() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    api.get('/admin/booked-calls/confirmation').then(r => setCfg(r.data)).catch(() => setNote('The confirmation settings could not load.'));
  }, []);

  const save = async (body, done) => {
    setBusy(true); setNote(null);
    try { const r = await api.put('/admin/booked-calls/confirmation', body); setCfg(r.data); setNote(done); }
    catch (e) { setNote(e.response?.data?.error || 'Could not save. Please try again.'); }
    finally { setBusy(false); }
  };
  const sample = async () => {
    setBusy(true); setNote(null);
    try { const r = await api.post('/admin/booked-calls/confirmation/sample'); setNote(`Sample sent to ${r.data.sentTo}, using ${r.data.basedOn}.`); }
    catch (e) { setNote(e.response?.data?.error || 'Could not send the sample.'); }
    finally { setBusy(false); }
  };
  const setReply = (i, field, value) => setCfg(c => ({ ...c, replies: c.replies.map((r, j) => (j === i ? { ...r, [field]: value } : r)) }));

  if (!cfg) return note ? <p className="text-sm text-gray-500 mb-3">{note}</p> : null;
  return (
    <div className="bg-white rounded-2xl shadow-sm px-5 py-4 mb-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium text-reset-brown">Our confirmation email</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {cfg.on
              ? 'On. Everyone who books gets your email within 5 minutes: their words, your reply, their time zone and add-to-calendar.'
              : 'Off. Nothing is sent. Read a sample first, then switch it on.'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          <button onClick={sample} disabled={busy} className="text-xs font-medium px-3 py-1.5 rounded-full border border-reset-terracotta text-reset-terracotta hover:bg-reset-cream disabled:opacity-50">Send me a sample</button>
          <button onClick={() => save({ on: !cfg.on }, cfg.on ? 'Switched off.' : 'Switched on. New bookings from now on get your email.')} disabled={busy}
            className={`text-xs font-medium px-3 py-1.5 rounded-full text-white disabled:opacity-50 ${cfg.on ? 'bg-gray-500' : 'bg-reset-sage'}`}>
            {cfg.on ? 'Switch off' : 'Switch on'}
          </button>
        </div>
      </div>
      <button onClick={() => setOpen(o => !o)} className="text-xs font-medium text-reset-terracotta hover:underline mt-3">
        {open ? '▾ Hide your replies' : `▸ Your replies for each topic (${cfg.replies.length})`}
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          <p className="text-xs text-gray-500">The email picks the topic whose words appear in what they wrote. The last topic is used when nothing matches. Your closing line is always added after the reply.</p>
          {cfg.replies.map((r, i) => (
            <div key={i} className="bg-reset-cream rounded-xl px-4 py-3 space-y-2">
              <label className="block text-xs font-medium text-gray-600">Topic
                <input value={r.topic} onChange={e => setReply(i, 'topic', e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
              </label>
              <label className="block text-xs font-medium text-gray-600">Words that point to this topic (separated by commas)
                <input value={r.words} onChange={e => setReply(i, 'words', e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
              </label>
              <label className="block text-xs font-medium text-gray-600">Your reply
                <textarea value={r.reply} onChange={e => setReply(i, 'reply', e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
              </label>
              {cfg.replies.length > 1 && (
                <button onClick={() => setCfg(c => ({ ...c, replies: c.replies.filter((_, j) => j !== i) }))} className="text-xs text-gray-500 hover:text-reset-terracotta">Remove this topic</button>
              )}
            </div>
          ))}
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setCfg(c => ({ ...c, replies: [...c.replies.slice(0, -1), { topic: '', words: '', reply: '' }, c.replies[c.replies.length - 1]] }))}
              className="text-xs font-medium px-3 py-1.5 rounded-full border border-gray-300 text-gray-600">+ Add a topic</button>
            <button onClick={() => save({ replies: cfg.replies }, 'Replies saved.')} disabled={busy}
              className="text-xs font-medium px-3 py-1.5 rounded-full bg-reset-terracotta text-white disabled:opacity-50">Save replies</button>
          </div>
        </div>
      )}
      {note && <p className="text-xs text-gray-600 mt-3">{note}</p>}
    </div>
  );
}

export default function BookedCalls() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    api.get('/admin/booked-calls')
      .then(r => setData(r.data))
      .catch(() => setError('Booked calls could not load just now. Try Refresh in a minute.'));
  }, []);

  const now = Date.now();
  const calls = data?.calls || [];
  const upcoming = calls.filter(c => new Date(c.end || c.start).getTime() >= now);
  const past = calls.filter(c => new Date(c.end || c.start).getTime() < now).reverse();

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold text-reset-brown mb-2">Booked Calls</h2>
      <ConfirmationSettings />
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
        {error && <p className="text-sm text-gray-500 px-5 py-4">{error}</p>}
        {!data && !error && <p className="text-sm text-gray-400 px-5 py-4">Loading…</p>}
        {data && !data.found && (
          <p className="text-sm text-gray-500 px-5 py-4">
            {data.reason === 'calendar_not_found'
              ? 'The Booked Calls calendar isn’t visible to Team365 yet, so calls can’t show here.'
              : 'Google Calendar isn’t connected for Team365, so calls can’t show here.'}
          </p>
        )}
        {data?.found && upcoming.length === 0 && (
          <p className="text-sm text-gray-500 px-5 py-4">No calls booked yet.</p>
        )}
        {data?.found && upcoming.map(c => <CallRow key={c.id} call={c} />)}
      </div>
      {data?.found && past.length > 0 && (
        <div className="mt-2">
          <button onClick={() => setShowPast(o => !o)} className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-2xl shadow-sm text-sm font-medium text-gray-500 hover:text-reset-brown transition-colors">
            <span>{showPast ? '▾' : '▸'} Calls in the last 30 days</span>
            <span className="text-gray-300">{past.length}</span>
          </button>
          {showPast && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50 mt-2">
              {past.map(c => <CallRow key={c.id} call={c} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
